import type { EventPayload } from '@side/analytics';

export enum TrackOfferEvent {
  ClickCreateOfferNext,
  CreateOfferBuyerStepLanding,
  ClickCreateOfferConfirmation,
  ClickNewBuyerButton,
  ClickExistingBuyerButton,
  SelectExistingBuyer,
  ClearExistingBuyer,
  ClickBuyerDetailsLink,
  SelectBuyerAgent,
  ClearBuyerAgent,
  SelectBuyerType,
  ClickAddBuyer,
  ClickRemoveBuyer,
  ShowMyListingResult,
  SelectMyListingResult,
  ListingMatchModalMount,
  ClickUseListing,
  DismissListingMatchModal,
  MyListingSummaryMountStep1,
  ClickChangeProperty,
  MyListingSummaryMountStep2,
  SellerAgentAutofillCardMount,
  LinkedListingAlertMount,
  DismissLinkedListingAlert,
}

type EventTypeLabels = Record<TrackOfferEvent, string>;

export const OfferEventLabels: EventTypeLabels = {
  [TrackOfferEvent.ClickCreateOfferNext]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.CreateOfferBuyerStepLanding]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ClickCreateOfferConfirmation]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ClickNewBuyerButton]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ClickExistingBuyerButton]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.SelectExistingBuyer]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ClearExistingBuyer]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ClickBuyerDetailsLink]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.SelectBuyerAgent]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ClearBuyerAgent]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.SelectBuyerType]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ClickAddBuyer]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ClickRemoveBuyer]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ShowMyListingResult]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.SelectMyListingResult]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ListingMatchModalMount]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ClickUseListing]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.DismissListingMatchModal]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.MyListingSummaryMountStep1]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.ClickChangeProperty]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.MyListingSummaryMountStep2]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.SellerAgentAutofillCardMount]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.LinkedListingAlertMount]: 'ADDRESS_DECOUPLER',
  [TrackOfferEvent.DismissLinkedListingAlert]: 'ADDRESS_DECOUPLER',
};

type EventTypePayloads = Record<TrackOfferEvent, EventPayload>;

export const OfferEventsMap: EventTypePayloads = {
  [TrackOfferEvent.ClickCreateOfferNext]: {
    eventContainer: 'address_decoupler_view',
    eventTarget: 'next_button',
    eventType: 'click',
    task: 'navigation',
  },
  [TrackOfferEvent.CreateOfferBuyerStepLanding]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'offer_buyer_details_view',
    eventType: 'load',
    task: 'view',
  },
  [TrackOfferEvent.ClickCreateOfferConfirmation]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'create_offer_button',
    eventType: 'click',
    task: 'add',
  },
  [TrackOfferEvent.ClickNewBuyerButton]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'new_buyer_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackOfferEvent.ClickExistingBuyerButton]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'existing_buyer_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackOfferEvent.SelectExistingBuyer]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'existing_buyer_dropdown',
    eventType: 'click',
    task: 'select',
  },
  [TrackOfferEvent.ClearExistingBuyer]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'clear_existing_buyer_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackOfferEvent.ClickBuyerDetailsLink]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'buyer_agreement_details_link',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackOfferEvent.SelectBuyerAgent]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'buyer_agent_dropdown',
    eventType: 'click',
    task: 'select',
  },
  [TrackOfferEvent.ClearBuyerAgent]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'clear_buyer_agent_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackOfferEvent.SelectBuyerType]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'buyer_type',
    eventType: 'click',
    task: 'select',
  },
  [TrackOfferEvent.ClickAddBuyer]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'add_buyer_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackOfferEvent.ClickRemoveBuyer]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'remove_buyer_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackOfferEvent.ShowMyListingResult]: {
    eventContainer: 'address_decoupler_view',
    eventTarget: 'my_listing_result',
    eventType: 'load',
    task: 'view',
  },
  [TrackOfferEvent.SelectMyListingResult]: {
    eventContainer: 'address_decoupler_view',
    eventTarget: 'my_listing_result',
    eventType: 'click',
    task: 'select',
  },
  [TrackOfferEvent.ListingMatchModalMount]: {
    eventContainer: 'address_decoupler_view',
    eventTarget: 'listing_match_modal',
    eventType: 'load',
    task: 'view',
  },
  [TrackOfferEvent.ClickUseListing]: {
    eventContainer: 'listing_match_modal',
    eventTarget: 'use_listing_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackOfferEvent.DismissListingMatchModal]: {
    eventContainer: 'listing_match_modal',
    eventTarget: 'close_button',
    eventType: 'click',
    task: 'exit',
  },
  [TrackOfferEvent.MyListingSummaryMountStep1]: {
    eventContainer: 'address_decoupler_view',
    eventTarget: 'my_listing_summary',
    eventType: 'load',
    task: 'view',
  },
  [TrackOfferEvent.ClickChangeProperty]: {
    eventContainer: 'my_listing_summary',
    eventTarget: 'change_property_button',
    eventType: 'click',
    task: 'delete',
  },
  [TrackOfferEvent.MyListingSummaryMountStep2]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'my_listing_summary',
    eventType: 'load',
    task: 'view',
  },
  [TrackOfferEvent.SellerAgentAutofillCardMount]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'seller_agent_autofill_card',
    eventType: 'load',
    task: 'view',
  },
  [TrackOfferEvent.LinkedListingAlertMount]: {
    eventContainer: 'offer_buyer_details_view',
    eventTarget: 'linked_listing_alert',
    eventType: 'load',
    task: 'view',
  },
  [TrackOfferEvent.DismissLinkedListingAlert]: {
    eventContainer: 'linked_listing_alert',
    eventTarget: 'close_button',
    eventType: 'click',
    task: 'exit',
  },
};
